import React, { useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import InputMask from "react-input-mask";

import { Listbox } from "@headlessui/react";
import { ChevronDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import CommonDatePicker, { CommonDateTimePicker } from "./DatePicker";
import DateTimePicker from "./DateTimePicker";
import { XMarkIcon } from "@heroicons/react/24/solid";

import { capitalizeFirstLetter as capitalizeFun } from "../../helpers/functions";
import NewToolTip from "./New-ToolTip";
import DateWithOptionsInput from "./DateWithOptionsInput";

/**
 * CustomInput - A versatile input component supporting multiple input types
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.label - Label text for the input field
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {string} props.name - Input name attribute
 * @param {string} props.type - Input type (text, password, email, etc.)
 * @param {Array} props.options - Options for dropdown/radio/checkbox inputs
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.error - Whether to show error styling
 * @param {boolean} props.vertical - Whether to stack radio options vertically
 * @param {Function} props.onChange - Change handler function
 * @param {Function} props.handleSelect - Select handler for dropdowns
 * @param {any} props.value - Current input value
 * @param {any} props.selectValue - Selected value for dropdowns
 * @param {boolean} props.focus - Focus state
 * @param {boolean} props.autoFocus - Whether to auto-focus the input
 * @param {Function} props.onFocus - Focus handler function
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.mask - Input mask pattern
 * @param {boolean} props.showOptionalLabel - Whether to show "(Optional)" text
 * @param {boolean} props.showInputCancellation - Whether to show close button
 * @param {Function} props.onClose - Close handler function
 * @param {Function} props.onClear - Clear handler function
 * @param {boolean} props.extraRadioSpace - Whether to add extra spacing for radio options
 * @param {boolean} props.showInfo - Whether to show info icon
 * @param {ReactNode} props.tooltip - Tooltip content
 * @param {string} props.labelClassName - Extra classes to apply to the label element
 * @param {string} props.inputClassName - Additional CSS classes for the input element
 * @param {boolean} props.capitalizeFirstLetter - Whether to capitalize first letter
 * @param {string} props.autoComplete - Autocomplete attribute value
 * @param {string} props.menuPlacement - Dropdown menu placement (top/bottom)
 * @param {string} props.initialOptions - Initial options display mode
 * @param {boolean} props.shortMenuHeight - Whether to use short menu height
 * @param {boolean} props.clearableDatePicker - Whether date picker is clearable
 * @param {boolean} props.hideHistory - Whether to hide autocomplete history
 * @param {boolean} props.NewDesign - When true, shows floating label design for text/password/email inputs
 * @param {boolean} props.showTime - When true and type is "date", enables time picker
 * @param {boolean} props.showDateOptions - When true and type is "date", shows calendar dropdown options on right
 * @param {string} props.dateOptionValue - Current date option value (for date with options)
 * @param {Function} props.onDateOptionChange - Callback when date option changes: (dateValue, dateOptionValue) => {}
 * @returns {JSX.Element} The rendered input component
 */
const CustomInput = ({
  className = "",
  label,
  placeholder,
  name,
  type,
  options,
  required = false,
  error = false,
  vertical = false,
  onChange,
  handleSelect = () => { },
  value,
  selectValue,
  focus = false,
  autoFocus = false,
  onFocus = () => { },
  disabled = false,
  mask = "",
  showOptionalLabel = true,
  showInputCancellation = undefined,
  onClose,
  onClear = () => { },
  extraRadioSpace = false,
  showInfo = false,
  tooltip,
  inputClassName = "",
  capitalizeFirstLetter = false,
  autoComplete = type === 'password' ? 'new-password' : 'on',
  menuPlacement = "bottom", //options: top, bottom
  initialOptions = "always",
  shortMenuHeight = false,
  clearableDatePicker = true, //set to true
  hideHistory = false, // New parameter to disable autocomplete history
  NewDesign = false, // New prop for border label design - when true, shows label on border for text/password inputs
  showPasswordTooltip = true, // Allows for tooltip to be hidden when type=password
  passwordToggleLabel = null, // Custom label for password toggle tooltip (e.g., "Username" instead of full label)
  dropdownInFlow = false, // When true, render dropdown options in normal flow so containers can expand
  selectedDisplayLabel = undefined,
  labelClassName = "",
  customMaxHeight = null, // Custom max height for dropdown (e.g., "200px" or "max-h-[200px]")
  showTime = false, // When true and type is "date", enables time picker
  showDateOptions = false, // When true and type is "date", shows calendar dropdown options
  dateOptionValue = null, // Current date option value
  onDateOptionChange = null, // Callback: (dateValue, dateOptionValue) => {}
  isIntervention = false, // When true, applies intervention-specific styling
  closeOnSelectValues = null, // When set (array of values), dropdown/multi-dropdown closes after selecting one of these values
  exclusiveSelectValues = null, // When set (array of values), selecting one of these in multi-dropdown clears all others and keeps only this value, then closes
  disableFuture = false, // When true and type is "date", disables future dates in the calendar
  accept,
  maxSize,
}) => {
  // State to track if input is focused or has value (for floating label)
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  // State for password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // State for dropdown showAll (must be at top level per React Hooks rules)
  // Initialize based on initialOptions prop - only used when type is "dropdown"
  const [showAll, setShowAll] = useState(
    initialOptions === "always"
  );

  // Ref to store Listbox button element for programmatic click (must be at top level per React Hooks rules)
  const multiDropdownButtonRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check if input has value on mount and value changes
  useEffect(() => {
    setHasValue(value && value.toString().trim().length > 0);
  }, [value]);

  // Helper function to get appropriate tooltip text for password toggle
  const getToggleTooltipText = () => {
    // Use custom passwordToggleLabel if provided, otherwise use the full label
    const fieldLabel = passwordToggleLabel || label;
    return showPassword ? `Hide ${fieldLabel}` : `Show ${fieldLabel}`;
  };
  
  const inputClass = `block w-full border-[0px] bg-transparent focus:outline-[0px] focus:ring-[0px] text-gray-800 placeholder:text-gray-200 p-0 focus:outline-none disabled:cursor-not-allowed ${inputClassName}`;

  // Small helper to render the info icon, chevron and tooltip (used by multiple dropdown variants)
  const renderInfoChevron = (open) => (
    <div className="flex flex-col">
      {showInfo && (
        <ReactSVG
          src="/icons/information-dark.svg"
          className="w-5 mr-3"
          data-tooltip-id={name}
        />
      )}
      <span>
        <ChevronDownIcon
          className={`w-5 transform transition-all ${open ? "-rotate-180" : ""}`}
        />
      </span>
      {!open && showInfo && <NewToolTip id={name} render={() => tooltip} />}
    </div>
  );

  // Adjust padding for new design to accommodate floating label
  const getInputClass = (inputType) => {
    if (NewDesign && (inputType === "text" || inputType === "password" || inputType === "email")) {
      return inputClass + " pt-1 pb-1"; // Reduced padding for better balance
    }
    return inputClass;
  };

  // Determine if label should be floating (focused or has value)
  const shouldFloatLabel = NewDesign && (type === "text" || type === "password" || type === "email") && (isFocused || hasValue);
  const labelIsElement = React.isValidElement(label);
  let inputElement;

  const handleChange = (e) => {
    if (capitalizeFirstLetter) {
      e.target.value = capitalizeFun(e.target.value);
    }
    onChange(e);
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
  };

  //For header-dropdown type
  const [expandedCategories, setExpandedCategories] = useState([]);

  switch (type) {
    case "password":
      inputElement = (
        <InputMask
          mask={mask}
          type={showPassword ? "text" : "password"}
          name={name}
          id={name}
          value={
            capitalizeFirstLetter && value && value.length > 0
              ? capitalizeFun(value)
              : value
          }
          className={getInputClass("password")}
          placeholder={shouldFloatLabel ? "" : (placeholder || label)}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          focus={focus}
          autoFocus={autoFocus}
          autoComplete={hideHistory ? "off" : autoComplete || "new-password"}
          data-lpignore="true"
          data-1p-ignore
        />
      );
      break;
    case "text":
      inputElement = (
        <div className="relative">
          <InputMask
            autoComplete={hideHistory ? "off" : autoComplete}
            mask={mask}
            type="text"
            name={name}
            id={name}
            value={
              capitalizeFirstLetter && value && value.length > 0
                ? capitalizeFun(value)
                : value
            }
            className={getInputClass("text") + " text-REELTeal"}
            placeholder={shouldFloatLabel ? "" : (placeholder || label)}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            focus={focus}
            autoFocus={autoFocus}
            {...((name?.toLowerCase()?.includes('password') || autoComplete === 'new-password') && {
              'data-lpignore': 'true',
              'data-1p-ignore': true,
            })}
          />
          {showInputCancellation && (
            <ReactSVG
              src="/icons/input-cross.svg"
              className="absolute top-0 right-0 mt-1 mr-2 hover:text-gray-700"
              onClick={() => {
                onClose();
              }}
            />
          )}
        </div>
      );
      break;
    case "textarea":
      inputElement = (
        <div className="relative">
          <textarea
            name={name}
            id={name}
            value={value}
            className={inputClass}
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            focus={focus}
            autoFocus={autoFocus}
          />
          {showInputCancellation && (
            <ReactSVG
              src="/icons/input-cross.svg"
              className="absolute top-0 right-0 mt-1 mr-2 hover:text-gray-700"
              onClick={() => {
                onClose();
              }}
            />
          )}
        </div>
      );
      break;
    case "date":
      if (showDateOptions) {
        inputElement = (
          <DateWithOptionsInput
            name={name}
            id={name}
            value={value}
            dateOptionValue={dateOptionValue}
            onDateOptionChange={onDateOptionChange}
            placeholder={placeholder}
            disabled={disabled}
            inputClassName={inputClassName}
            isIntervention={isIntervention}
          />
        );
      } else {
        // Standard date picker
        inputElement = (
          <>
            {showTime ? (
              <CommonDateTimePicker
                onChange={onChange}
                name={name}
                id={name}
                value={value}
                onFocus={onFocus}
                disabled={disabled}
                inputClass={inputClass}
                clearableDatePicker={clearableDatePicker}
              />
            ) : (
              <CommonDatePicker
                onChange={onChange}
                name={name}
                id={name}
                value={value}
                onFocus={onFocus}
                disabled={disabled}
                inputClass={inputClass}
                clearableDatePicker={clearableDatePicker}
                disableFuture={disableFuture}
              />
            )}
          </>
        );
      }
      break;
    case "datetime":
      inputElement = (
        <DateTimePicker
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          disabled={disabled}
          inputClass={inputClass}
          clearableDatePicker={clearableDatePicker}
          label={label}
          matchCustomInputStyle
        />
      );
      break;
    case "age":
      inputElement = (
        <div className="relative">
          <input
            focus={focus}
            autoFocus={autoFocus}
            name={name}
            id={name}
            type="number"
            value={value}
            className={inputClass}
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
          />
          {showInputCancellation && (
            <>
              <div
                className="absolute right-5 top-1/3 h-6 w-px -translate-y-1/3 bg-gray-200 pointer-events-none"
                aria-hidden="true"
              />
              <ReactSVG
                src="/icons/input-cross.svg"
                className="absolute top-0 right-0 mt-2 mr-8 hover:text-gray-700 cursor-pointer"
                onClick={() => {
                  onClose();
                }}
              />
            </>
          )}
        </div>
      );
      break;
    case "multi-dropdown":
      const DefaultOpt = {
        label: `Select ${placeholder ? placeholder : ""}`,
        value: "",
      };
      // Ensure value is always an array before using array methods
      const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
      const multiOptionDisplay = (opt) => {
        if (!opt?.label) return "";
        const sub = opt.subLabel?.trim();
        return sub ? `${opt.label} — ${sub}` : opt.label;
      };
      let selectedLabel = valueArray.length > 0 ?
        valueArray
          .filter((val) => val !== null && val !== undefined && String(val).trim().length > 0)
          .map((val) => multiOptionDisplay(options?.find((option) => option.value === val)))
          .filter(Boolean)
          .join(", ") : DefaultOpt.label;
      const multiShouldShowClear = valueArray.length > 0 && valueArray.some((v) => v !== null && v !== undefined && String(v).trim().length > 0);
      const multiDropdownShowInputCancellation = showInputCancellation === undefined ? true : showInputCancellation;
      
      const noneOption = options?.find(
        (opt) =>
          opt.value?.toString().toLowerCase() === "none" ||
          opt.label?.toString().toLowerCase() === "none"
      );
      const exclusiveValues = [
        ...(noneOption ? [noneOption.value] : []),
        ...(Array.isArray(exclusiveSelectValues) ? exclusiveSelectValues : []),
      ];

      const handleMultiSelectChange = (selectedValues) => {
        let selectedArray = Array.isArray(selectedValues) ? selectedValues : (selectedValues ? [selectedValues] : []);
        const currentValueArray = Array.isArray(value) ? value : (value ? [value] : []);

        const hasExclusive = exclusiveValues.length > 0 && selectedArray.some((v) => exclusiveValues.includes(v));
        const hasNonExclusive = selectedArray.some((v) => !exclusiveValues.includes(v));
        const hadExclusiveOnly =
          exclusiveValues.length > 0 &&
          currentValueArray.some((v) => exclusiveValues.includes(v)) &&
          !currentValueArray.some((v) => !exclusiveValues.includes(v));

        const keepOnlyExclusive =
          hasExclusive && (!hasNonExclusive || !hadExclusiveOnly);
        if (keepOnlyExclusive) {
          const exclusiveValue = selectedArray.find((v) => exclusiveValues.includes(v));
          onChange([exclusiveValue]);
          setTimeout(() => multiDropdownButtonRef.current?.click(), 50);
          return;
        }
        if (hasExclusive && hasNonExclusive && hadExclusiveOnly) {
          selectedArray = selectedArray.filter((v) => !exclusiveValues.includes(v));
        }

        let valuesToSet = selectedArray.filter((v) => !exclusiveValues.includes(v));
        const didExclusiveClose = hasExclusive && !hasNonExclusive;

        onChange(valuesToSet);

        const shouldClose =
          !didExclusiveClose &&
          Array.isArray(closeOnSelectValues) &&
          closeOnSelectValues.length > 0 &&
          valuesToSet.some((v) => closeOnSelectValues.includes(v));
        if (shouldClose) {
          setTimeout(() => multiDropdownButtonRef.current?.click(), 50);
        }
      };
      
      inputElement = (
        <div className="relative -mx-3">
          <Listbox
            disabled={disabled}
            value={valueArray}
            onChange={handleMultiSelectChange}
            multiple
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  ref={multiDropdownButtonRef}
                  className={`${inputClass} text-left px-3 text-body-md flex items-center justify-between`}
                >
                  <span className="min-w-0 flex-1 pr-2 text-wrap break-words">{selectedLabel}</span>
                  {!disabled && (
                    <div className="flex items-center gap-2">
                      {multiDropdownShowInputCancellation && multiShouldShowClear && (
                        <>
                          <ReactSVG
                            src="/icons/input-cross.svg"
                            className="w-3 h-3 cursor-pointer hover:text-gray-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange([]);
                              onClear();
                              setTimeout(() => multiDropdownButtonRef.current?.click(), 50);
                            }}
                          />
                          <div className="h-5 w-px bg-gray-200"></div>
                        </>
                      )}
                      {renderInfoChevron(open)}
                    </div>
                  )}
                </Listbox.Button>
                <Listbox.Options
                  className={`${dropdownInFlow
                    ? `relative left-0 w-full bg-white shadow-lg mt-2 z-10 rounded-t-0 ${options?.length <= 5 && !customMaxHeight ? "overflow-visible" : `overflow-y-auto ${customMaxHeight ? customMaxHeight : "max-h-40"} overscroll-contain`}`
                    : `absolute left-0 w-full bg-white shadow-lg top-full z-50 mt-1 rounded-t-0 ${options?.length <= 5 && !customMaxHeight ? "overflow-visible" : `overflow-y-auto ${customMaxHeight ? customMaxHeight : "max-h-72"} overscroll-contain`}`
                  }`}
                >
                  {options &&
                    options?.map((option, index) => (
                      <Listbox.Option
                        key={index}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {({ active, selected }) => (
                          <div
                            className={`flex flex-col border-b border-gray-20 py-2.5 px-3 text-sm
                            ${option.disabled
                                ? "cursor-not-allowed text-gray-500"
                                : selected
                                  ? "cursor-pointer bg-teal-100 text-white hover:bg-teal-100 hover:text-white"
                                  : "cursor-pointer text-gray-800 hover:bg-blue-100"
                              } ${active ? "activeClass" : ""}`}
                          >
                            <span>{option.label}</span>
                            {option.subLabel ? (
                              <p className="mt-0.5 text-xs italic opacity-90">{option.subLabel}</p>
                            ) : null}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </>
            )}
          </Listbox>
        </div>
      );
      break;
    case "category-dropdown":
      const [selectedCategory, selectedValue] = value
        ? value.split("__")
        : [null, null];
      const selectedOption = options
        ?.find((category) => category.name === selectedCategory)
        ?.options?.find((x) => x.value === selectedValue);

      useEffect(() => {
        if (selectedCategory) {
          setExpandedCategories([selectedCategory]);
        }
      }, [selectedCategory]);
      inputElement = (
        <div className="relative -mx-3 ">
          <Listbox
            value={selectedOption}
            // onChange={onChange}
            disabled={disabled}
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  className={
                    inputClass +
                    " text-left px-3 text-body-md flex items-center justify-between"
                  }
                >
                  <span>
                    {!selectedOption && label != "" && (
                      <>
                        {"Select "} {label}
                      </>
                    )}
                    {selectedOption && (
                      <>
                        {selectedCategory} {" -> "}
                        {selectedOption?.label}
                        {selectedOption?.subLabel && (
                          <>
                            <br />
                            <p className="italic">{selectedOption?.subLabel}</p>
                          </>
                        )}
                      </>
                    )}
                  </span>
                  {!disabled && (
                    <div className="flex flex-col">
                      {showInfo && (
                        <ReactSVG
                          src="/icons/information-dark.svg"
                          className="w-5 mr-3"
                          data-tooltip-id={name}
                        />
                      )}
                      <span>
                        <ChevronDownIcon
                          className={`w-5 transform transition-all ${open ? "-rotate-180" : ""}`}
                        />
                      </span>
                      {!open && showInfo && <NewToolTip id={name} render={() => tooltip} place="left" />}
                    </div>
                  )}
                </Listbox.Button>
                <Listbox.Options
                  className={`absolute left-0 w-full bg-white shadow-lg top-9 z-50 rounded-t-0 overflow-auto max-h-75 ${menuPlacement === "top" &&
                    "transform -translate-y-full mt-2"
                    }`}
                >
                  {options.map((category, catIndex) => (
                    <div key={catIndex} className="py-1">
                      {/* Category Header */}
                      <div
                        onClick={() => {
                          if (expandedCategories.includes(category.name))
                            setExpandedCategories(
                              expandedCategories.filter(
                                (x) => x !== category.name
                              )
                            );
                          else
                            setExpandedCategories([
                              ...expandedCategories,
                              category.name,
                            ]);
                        }}
                        className="px-4 py-2 font-bold text-gray-700 cursor-pointer hover:bg-gray-100"
                      >
                        {category.name}
                      </div>
                      {/* Conditionally show options if category is expanded */}
                      {expandedCategories.includes(category.name) &&
                        category.options?.map((option, index) => (
                          <Listbox.Option
                            key={index}
                            value={option}
                            disabled={option.disabled}
                            onClick={(e) => {
                              onChange({
                                ...option,
                                value: category.name + "__" + option.value,
                              });
                            }}
                          >
                            {({ active, selected }) => (
                          <div
                                className={`flex flex-col justify-between text-sm overflow-hidden py-2.5 px-3 
                                    ${option.disabled
                                    ? "text-gray-500 cursor-not-allowed"
                                    : "text-gray-800 cursor-pointer hover:bg-blue-100"
                                  } ${active ? "activeClass" : ""} 
                                    ${selected ? "bg-teal-100 text-white" : ""
                                  }`}
                              >
                                {option.label}
                                {option.subLabel && (
                                  <>
                                    <br />
                                    <p className="italic">{option.subLabel}</p>
                                  </>
                                )}
                              </div>
                            )}
                          </Listbox.Option>
                        ))}
                    </div>
                  ))}
                </Listbox.Options>
              </>
            )}
          </Listbox>
        </div>
      );
      break;
    case "dropdown":
      //initialOptions is not always then show only options with showAlways: initialOptions | "always" [if not given show all options as default]
      const defaultOption = {
        label: `Select ${placeholder ? placeholder : ""}`,
        value: "",
      };
      const selected = options?.find((option) => option.value === value) || defaultOption;
      const displayedLabel = selectedDisplayLabel ?? selected.label ;
      const shouldShowClear = selected && selected !== defaultOption && selected.value != null && String(selected.value).trim() !== "";
      // For dropdowns, default to showing the input cancellation icon unless explicitly disabled
      const dropdownShowInputCancellation = showInputCancellation === undefined ? true : showInputCancellation;
      const handleDropdownChange = (option) => {
        onChange(option);
        if (Array.isArray(closeOnSelectValues) && closeOnSelectValues.length > 0 && option?.value != null && closeOnSelectValues.includes(option.value)) {
          setTimeout(() => {
            dropdownButtonRef.current?.click();
          }, 50);
        }
      };
      inputElement = (
        <div className="relative -mx-3 ">
          <Listbox
            disabled={disabled}
            value={selected.value}
            onChange={handleDropdownChange}
          // className="border-yellow-200"
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  ref={dropdownButtonRef}
                  className={
                    inputClass +
                    " text-left px-3 text-body-md flex items-center justify-between"
                  }
                >
                  <span className={`${selected.value === "" && !selectedDisplayLabel ? "text-gray-400" : ""} whitespace-nowrap`}>
                    {displayedLabel}
                    {selected.subLabel && !selectedDisplayLabel && (
                      <>
                        <br />
                        <p className="italic">{selected.subLabel}</p>
                      </>
                    )}
                  </span>
                  {!disabled && (
                    <div className="flex items-center gap-2">
                      {dropdownShowInputCancellation && shouldShowClear && (
                        <>
                          <ReactSVG
                            src="/icons/input-cross.svg"
                            className="w-3 h-3 cursor-pointer hover:text-gray-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange({ value: "", label: "" });
                              onClear();
                            }}
                          />
                          <div className="h-5 w-px bg-gray-200"></div>
                        </>
                      )}
                      {renderInfoChevron(open)}
                    </div>
                  )}
                </Listbox.Button>
                <Listbox.Options
                  className={`${dropdownInFlow
                    ? `relative left-0 w-full bg-white shadow-lg mt-2 z-10 rounded-t-0 ${options?.length <= 5 && !customMaxHeight ? "overflow-visible" : `overflow-y-auto ${customMaxHeight ? customMaxHeight : (shortMenuHeight ? "max-h-32" : "max-h-40")} overscroll-contain`}`
                    : `absolute left-0 w-full bg-white shadow-lg top-9 z-50 rounded-t-0 ${options?.length <= 3 ? "overflow-visible" : `overflow-y-auto ${customMaxHeight ? customMaxHeight : (shortMenuHeight ? "max-h-32" : "max-h-72")} overscroll-contain`} ${menuPlacement === "top" ? " transform -translate-y-full mt-2" : ""}`
                  }`}
                >
                  {options &&
                    (showAll === true
                      ? options
                      : options.filter(
                        (x) =>
                          x.showAlways === "always" ||
                          x.showAlways === initialOptions
                      )
                    )?.map((option, index) => (
                      <Listbox.Option
                        key={index}
                        value={option}
                        disabled={option.disabled}
                      >
                        {({ active, selected }) => (
                          <div
                            className={`flex flex-col justify-between text-sm overflow-hidden py-2.5 px-3 whitespace-pre 
                            ${option.disabled
                                ? "text-gray-500 cursor-not-allowed"
                                : selected
                                  ? "cursor-pointer bg-teal-100 text-white hover:bg-teal-100 hover:text-white"
                                  : "text-gray-800 cursor-pointer hover:bg-blue-100"
                              } ${active ? "activeClass" : ""}`}
                          >
                            {option.label}
                            {option.subLabel && (
                              <>
                                <br />
                                <p className="italic">{option.subLabel}</p>
                              </>
                            )}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                  {!showAll && (
                    <div
                      onClick={() => setShowAll(true)}
                      className="px-4 py-2 font-bold text-gray-700 underline cursor-pointer hover:bg-gray-100"
                    >
                      View All
                    </div>
                  )}
                </Listbox.Options>
              </>
            )}
          </Listbox>
        </div>
      );
      break;
    case "email":
      inputElement = (
        <div className="relative">
          <input
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            name={name}
            focus={focus}
            autoFocus={autoFocus}
            id={name}
            value={value}
            type="email"
            className={getInputClass("email")}
            placeholder={shouldFloatLabel ? "" : (placeholder || label)}
            disabled={disabled}
          />
          {showInputCancellation && (
            <ReactSVG
              src="/icons/input-cross.svg"
              className="absolute top-0 right-0 mt-1 mr-2 hover:text-gray-700"
              onClick={() => {
                onClose();
              }}
            />
          )}
        </div>
      );
      break;
    case "phone":
      inputElement = (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center">
            <label htmlFor="country" className="sr-only">
              Country
            </label>
            <select
              id="country"
              name="country"
              autoComplete="country"
              onChange={handleSelect}
              value={selectValue || "CA"}
              className="h-full bg-transparent px-2 py-0 focus:ring-[0px] focus:outline-[0px] border-[0px] w-[72px] rounded-md "
            >
              <option disabled value={"Select Country Code"}>
                Select Country Code
              </option>
              <option value={"+1"}>CA</option>
              <option value={"1"}>US</option>
            </select>
          </div>
          <InputMask
            type="tel"
            focus={focus}
            autoFocus={autoFocus}
            onFocus={onFocus}
            value={value}
            onChange={onChange}
            className={inputClass + ` pl-20 `}
            placeholder={placeholder}
            mask="(999) 999-9999"
          />
        </div>
      );
      break;
    case "postalCode":
      inputElement = (
        <input
          name={name}
          id={name}
          type="text"
          onChange={e => {
            let val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""); // Remove non-alphanumeric
            if (val.length > 3) {
              val = val.slice(0, 3) + " " + val.slice(3, 6);
            }
            if (val.length > 7) {
              val = val.slice(0, 7); // Limit to 7 chars (including space)
            }
            onChange({ target: { name, value: val } });
          }}
          focus={focus}
          autoFocus={autoFocus}
          value={value}
          className={inputClass}
          placeholder={placeholder}
          maxLength={7}
        />
      );
      break;
    case "weight":
      inputElement = (
        <div className="flex items-center space-x-3">
          <div className="relative">
            <InputMask
              type="number"
              focus={focus}
              autoFocus={autoFocus}
              value={value}
              onChange={onChange}
              className={inputClass + `pr-20 `}
              placeholder={placeholder}
            />
          </div>
        </div>
      );
      break;
    case "radio":
      return (
        <div className={` ${className}`}>
          <fieldset className="mb-3">
            <div
              className={`${vertical
                ? "flex-col"
                : `flex-row space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0 ${extraRadioSpace && "gap-16"
                } `
                } `}
            >
              {options.map((option, index) => (
                <div key={option.value} className="flex items-center">
                  <input
                    id={option.value + name + index}
                    name={name + index}
                    value={option.value}
                    type="radio"
                    defaultChecked={option.value === "email"}
                    checked={value === option.value}
                    onChange={onChange}
                    className="w-4 h-4 cursor-pointer text-teal-50 focus:ring-transparent"
                  />
                  <label
                    htmlFor={option.value + name + index}
                    className="block ml-3 text-teal-600 text-body-md"
                  >
                    {option.label}{" "}
                    {option?.redLabel && (
                      <span className="text-red-300">{option.redLabel}</span>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
        </div>
      );
    case "checkbox":
      return (
        <fieldset className={`${className}`}>
          <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
            {options.map((option, index) => (
              <div key={option.value || option.name || index} className="relative flex items-start">
                <div className="flex items-center h-6">
                  <input
                    id={option.name}
                    name={option.name}
                    onChange={onChange}
                    // checked={option.value}
                    type="checkbox"
                    checked={value}
                  />
                </div>
                <div className="ml-3 text-body-md ">
                  <label
                    htmlFor={option.name}
                    className="text-gray-900 cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </fieldset>
      );
    case "versionCode":
      inputElement = (
        <InputMask
          name={name}
          id={name}
          type="text"
          onChange={onChange}
          focus={focus}
          autoFocus={autoFocus}
          value={value}
          className={inputClass}
          placeholder={placeholder}
          disabled={disabled}
          mask="aa"
        />
      );
      break;
    case "file":
      inputElement = (
        <div className="inline-flex">
          <button
            type="button"
            className="inline-flex items-center px-[10px] py-[10px] text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white hover:bg-white focus:outline-none"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <ReactSVG
              src="/icons/upload.svg"
              className="w-[18px] h-[18px] mr-2"
            />
            {placeholder || "Upload Photo"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={accept || "image/jpeg,image/png,image/webp"}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;

              if (!file) {
                onChange(null);
                return;
              }

              const limit = maxSize || 5 * 1024 * 1024;
              const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

              if (!allowedTypes.includes(file.type) || file.size > limit) {
                onChange(null);
                return;
              }

              onChange(file);
            }}
          />
        </div>
      );
      break;
    default:
      inputElement = (
        <input
          name={name}
          id={name}
          onChange={onChange}
          focus={focus}
          autoFocus={autoFocus}
          type="text"
          value={value}
          className={inputClass}
          placeholder={placeholder}
        />
      );
      break;
  }

  // Special handling for password input to create input group layout
  if (type === "password") {
    return (
      <div className={`relative rounded-[4px] px-3 py-3 border-[1px] border-gray-100 
      ${error ? "border-red-200 border-[2px]" : "focus-within:border-[2px] focus-within:border-REELTeal"
        }  ${NewDesign && (type === "text" || type === "password" || type === "email") ? "transition-all duration-200 ease-out" : ""} ${className} ${disabled && "opacity-60"}`}>
        {/* Render label based on NewDesign prop */}
        {label && label != "" && !NewDesign && (
          <label htmlFor={name} className={`block mb-1 font-normal text-gray-800 text-body-xs ${labelClassName}`}>
            {label}{" "}
            {!disabled && !labelIsElement && (
              <>
                {required ? (
                  <span className="">{` *`}</span>
                ) : (
                  <span className="">{` ${showOptionalLabel ? "(Optional)" : ""}`}</span>
                )}
              </>
            )}
          </label>
        )}

        {/* New design with floating label for password input */}
        {NewDesign && isFocused && label && label !== "" && (
          <label
            htmlFor={name}
            className={`absolute left-3 bg-white px-1 pointer-events-none z-10
              transition-all duration-200 ease-out
              ${shouldFloatLabel
                ? "-top-2.5 text-[18px] text-teal-900 font-medium"
                : "top-3 text-gray-400 text-sm"}
            `}
          >
            {label}
            {!disabled && !labelIsElement && (
              <>
                {required ? (
                  <span className="ml-1 text-teal-900">*</span>
                ) : (
                  <span className="text-gray-400">
                    {` ${showOptionalLabel ? "(Optional)" : ""}`}
                  </span>
                )}
              </>
            )}
          </label>
        )}

        {/* Input group container */}
        <div className="relative flex">
          {/* Input field with left and top/bottom padding only */}
          <div className="flex-1 rounded-l-[4px]">
            {inputElement}
          </div>

          {/* Password toggle button with full height background */}
          <button
            type="button"
            className="w-14 flex items-center justify-center transition-colors duration-200 hover:bg-teal-75 group rounded-r-[4px] focus-visible:outline-none"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeIcon className="w-5 text-teal-900 group-hover:text-teal-900" />
            ) : (
              <EyeSlashIcon className="w-5 text-gray-600 group-hover:text-teal-900" />
            )}

            {showPasswordTooltip  &&
              // Tooltip positioned above the button 
            <div className="absolute bottom-full mb-1 px-1 py-1 text-[14px] text-gray-800 bg-white border border-teal-100 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                {getToggleTooltipText()}
                {/* Tooltip arrow */}
                <div className="absolute w-0 h-0 transform -translate-x-1/2 border-t-4 border-l-4 border-r-4 border-transparent top-full left-1/2 border-t-teal-100"></div>
              </div>
            }
          </button>
        </div>

        {/* Input cancellation button positioned above the input group */}
        {showInputCancellation && (
          <div className="absolute top-0 right-0 z-30">
            <ReactSVG
              src="/icons/input-cross.svg"
              className="mt-1 mr-2 cursor-pointer hover:text-gray-700"
              onClick={() => {
                onClose();
              }}
            />
          </div>
        )}
      </div>
    );
  }

  // Default return for all other input types
  return (
    <div
      className={`relative rounded-[4px] px-3 py-3 border-[1px] border-gray-100 
      ${error ? "border-red-200 border-[2px]" : "focus-within:border-[2px] focus-within:border-REELTeal"
        } 
      ${NewDesign && (type === "text" || type === "password" || type === "email") ? "transition-all duration-200 ease-out" : ""}
      ${className} ${disabled && "opacity-60"}`}
    >
      {/* Render label based on NewDesign prop */}
      {label && label != "" && type != "datetime" && !NewDesign && (
        <label htmlFor={name} className={`block mb-1 font-normal text-gray-900 text-xs leading-[18px] ${labelClassName}`}>
          {label}{" "}
          {!disabled && !labelIsElement && (
            <>
              {required ? (
                <span className="">{` *`}</span>
              ) : (
                <span className="">{` ${showOptionalLabel ? "(Optional)" : ""}`}</span>
              )}
            </>
          )}
        </label>
      )}

      {/* New design with floating label for text, password, and email inputs */}
      {NewDesign && (type === "text" || type === "password" || type === "email") && label && label !== "" && (
        <label
          htmlFor={name}
          className={`absolute left-3 bg-white px-1 pointer-events-none
            transition-all duration-200 ease-out
            ${shouldFloatLabel
              ? "-top-2.5 text-xs text-teal-900 font-medium"
              : "top-3 text-gray-400 text-sm"}  // Placeholder state
          `}
        >
          {label}
          {!disabled && !labelIsElement && (
            <>
              {required ? (
                <span className="ml-1 text-teal-900">*</span>
              ) : (
                <span className="text-gray-400">
                  {` ${showOptionalLabel ? "(Optional)" : ""}`}
                </span>
              )}
            </>
          )}
        </label>
      )}

      {inputElement}
    </div>
  );
};

export default CustomInput;